/**
 * KAN-744: раздел "Менеджеры": вкладки Активные / Архив, таблица менеджеров.
 * Добавлять, менять, архивировать и удалять может только владелец; остальные
 * видят список только для чтения.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, UserPlus, UserCheck } from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { Button } from '@/components/ui/button';
import { PillTabs, type PillTab } from '@/components/dashboard/PillTabs';
import { ManagersTable, type ManagerRowActions } from '@/components/managers/ManagersTable';
import { ManagerDialog } from '@/components/managers/ManagerDialog';
import { ManagerDeleteDialog } from '@/components/managers/ManagerDeleteDialog';
import { useIsOwner } from '@/hooks/useCurrentUser';
import { useManagerMutations, useManagers } from '@/hooks/useManagers';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import type { Manager } from '@/types/manager';

type ManagersTab = 'true' | 'false';

const TABS: readonly PillTab<ManagersTab>[] = [
  { value: 'true', label: 'Активные' },
  { value: 'false', label: 'Архив' },
];

function useOwnerActions() {
  const { update } = useManagerMutations();
  const [editing, setEditing] = useState<Manager | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState<Manager | null>(null);

  const actions: ManagerRowActions = {
    onEdit: (m) => {
      setEditing(m);
      setDialogOpen(true);
    },
    onToggleActive: (m) => {
      update.mutate(
        { id: m.id, data: { is_active: !m.is_active } },
        {
          onSuccess: () => toast.success(m.is_active ? 'Менеджер в архиве' : 'Менеджер возвращён'),
          onError: (err) => toast.error(extractErrorMessage(err, 'Не удалось сохранить')),
        }
      );
    },
    onDelete: setDeleting,
  };
  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  return { actions, openCreate, editing, dialogOpen, setDialogOpen, deleting, setDeleting };
}

export function ManagersPage(): JSX.Element {
  const isOwner = useIsOwner();
  const [tab, setTab] = useState<ManagersTab>('true');
  const { data: managers = [], isLoading, isError } = useManagers(tab);
  const owner = useOwnerActions();

  useEffect(() => {
    document.title = 'Менеджеры - SMETA AI';
  }, []);

  const addButton = isOwner ? (
    <Button data-testid="add-manager-button" onClick={owner.openCreate}>
      <UserPlus className="mr-2 h-4 w-4" />
      Добавить менеджера
    </Button>
  ) : undefined;

  let body: JSX.Element;
  if (isLoading) {
    body = (
      <div className="flex justify-center py-12" data-testid="managers-loading">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  } else if (isError) {
    body = (
      <p className="py-8 text-center text-sm text-red-600" data-testid="managers-error">
        Не удалось загрузить менеджеров
      </p>
    );
  } else if (managers.length === 0) {
    body = (
      <div className="flex flex-col items-center py-12 text-gray-500" data-testid="managers-empty">
        <UserCheck className="mb-3 h-10 w-10 text-gray-300" />
        <p className="text-sm">{tab === 'true' ? 'Менеджеров пока нет' : 'В архиве пусто'}</p>
      </div>
    );
  } else {
    body = <ManagersTable managers={managers} actions={isOwner ? owner.actions : undefined} />;
  }

  return (
    <PageLayout
      title="Менеджеры"
      breadcrumbs={[{ label: 'Менеджеры' }]}
      actions={addButton}
      data-testid="managers-page"
    >
      <div className="mb-4">
        <PillTabs label="Менеджеры" tabs={TABS} value={tab} onChange={setTab} testIdPrefix="managers-tab" />
      </div>
      {body}
      {isOwner && (
        <>
          <ManagerDialog
            open={owner.dialogOpen}
            onOpenChange={owner.setDialogOpen}
            manager={owner.editing}
          />
          <ManagerDeleteDialog
            manager={owner.deleting}
            onOpenChange={(open) => !open && owner.setDeleting(null)}
          />
        </>
      )}
    </PageLayout>
  );
}
